import React, { useState, useEffect, useContext } from 'react';

import { Row, Col } from 'antd';

import styled from 'styled-components';

import { getEventLocationIframeUrl, getEventDirectionUrl } from '@libs/NwGoogleMap';

import { ListContext } from '@contexts/ListContext';

import {
    useScoutingAccommodation,
    useDeleteScoutingAccommodation,
    forceReloadScoutingAccommodation,
} from '@hooks/API/Event/UseScoutingAccommodation';

import ObjectHistory from '@components/CreatedBy/ObjectHistory';
import EventDrawerHeader from '@components/EventDrawer/EventDrawerUi/EventDrawerHeader';
import { EventTitle } from '@components/EventDrawer/EventDrawerUi/EventTitle';
import { ObjectHistoryContainer, EventContainer } from '@components/EventDrawer/EventDrawerUi/EventUI';
import ScoutingAccommodationDetails from '@components/EventDrawer/ScoutingAccommodationEvent/ScoutingAccommodationDetails';
import ScoutingAccommodationForm from '@components/EventDrawer/ScoutingAccommodationEvent/ScoutingAccommodationForm';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';

const LocationUrl = styled.a`
    color: ${(props) => props.theme.PrimaryColor};
    text-decoration: underline;

    &:hover {
        color: ${(props) => props.theme.PrimaryColor};
        text-decoration: underline;
    }
`;

const ScoutingAccommodationEvent = (props) => {
    const {
        ID,
        viewMode,
        travelPlanId,
        afterUpdate,
        afterRemove,
        plainMode,
        dateSelected,
        allDay,
        onClose,
        onTouchForm,
    } = props;
    const [scoutingAccommodationID, setScoutingAccommodationID] = useState();
    const [isEdit, setIsEdit] = useState(false);
    const { data: scoutingAccommodationData, status } = useScoutingAccommodation(scoutingAccommodationID);
    const { mutateAsync: onDeleteScoutingAccommodation } = useDeleteScoutingAccommodation();
    const [editModelEvent, setEditModelEvent] = useState();
    const [touchForm, setTouchForm] = useState(false);
    const { cachedList } = useContext(ListContext);

    useEffect(() => {
        setScoutingAccommodationID(ID);
    }, [ID]);

    useEffect(() => {
        setIsEdit(viewMode ? true : false);
    }, [viewMode]);

    const handleRemoveEvent = async (eventId) => {
        try {
            await onDeleteScoutingAccommodation({ ID: eventId, scoutingAccommodationId: scoutingAccommodationID });
        } catch (error) {
            console.error('Remove Event Date Model Error => ', error);
        }
    };

    const HeaderContent = () =>
        scoutingAccommodationData ? (
            plainMode ? (
                <div />
            ) : (
                <EventDrawerHeader type={120} typeName="scoutingAccommodation" data={scoutingAccommodationData} />
            )
        ) : (
            <div className="ant-drawer-header ant-drawer-header-sticky ">
                <div className="ant-drawer-title">Add New Scouting Accommodation</div>
            </div>
        );

    const EventTitleContent = () => {
        const props = {
            actions: {
                onEditGeneral: () => {
                    setIsEdit(!isEdit);
                },
                onEdit: () => {
                    setIsEdit(!isEdit);
                },
                onDelete: async () => {
                    await handleRemoveEvent(scoutingAccommodationData.ID);
                    if (scoutingAccommodationData.ID === scoutingAccommodationID) {
                        afterRemove(true);
                    } else {
                        afterUpdate();
                    }
                },
            },
            eventProperties: {
                eventType: 'ScoutingAccommodation',
                isSingle: true,
            },
            event: scoutingAccommodationData,
        };
        return <EventTitle {...props} />;
    };

    const EventDetailsContent = () => (
        <>
            <EventTitleContent />
            <ScoutingAccommodationDetails
                type={'ScoutingAccommodation'}
                scoutingAccommodationData={scoutingAccommodationData}
            />
            <ObjectHistoryContainer>
                <ObjectHistory asLine object={scoutingAccommodationData} />
            </ObjectHistoryContainer>
        </>
    );

    const handleClearEditEvent = () => {
        if (editModelEvent) {
            setEditModelEvent();
        }
    };

    const handleClearTouchForm = () => {
        if (touchForm) {
            setTouchForm(false);
        }
    };

    const ScoutingAccommodationFormContent = () => (
        <ScoutingAccommodationForm
            plainMode={plainMode}
            scoutingAccommodationData={editModelEvent ? editModelEvent : scoutingAccommodationData}
            editModelEvent={editModelEvent ? true : false}
            viewMode={viewMode}
            dateSelected={dateSelected}
            allDay={allDay}
            travelPlanId={travelPlanId}
            afterUpdate={afterUpdate}
            onTouchForm={(touchForm) => {
                if (scoutingAccommodationData) {
                    setTouchForm(touchForm);
                } else {
                    onTouchForm(touchForm);
                }
            }}
            onCancel={() => {
                handleClearEditEvent();
                handleClearTouchForm();
                if (scoutingAccommodationData) {
                    setIsEdit(false);
                } else {
                    onClose();
                }
            }}
            onUpdateScoutingAccommodationData={(scoutingAccommodationId) => {
                if (scoutingAccommodationId) {
                    setScoutingAccommodationID(scoutingAccommodationId);
                } else {
                    forceReloadScoutingAccommodation(scoutingAccommodationID);
                }
                handleClearTouchForm();
                handleClearEditEvent();
            }}
        />
    );

    const Content = () =>
        plainMode ? (
            <>
                {scoutingAccommodationData && <EventDetailsContent />}
                {isEdit && (
                    <NwDrawer
                        padding="0px"
                        width={740}
                        touchForm={touchForm}
                        onTouchForm={setTouchForm}
                        title="Edit Scouting Accommodation"
                        onClose={() => {
                            handleClearEditEvent();
                            setIsEdit(false);
                            handleClearTouchForm();
                        }}
                    >
                        <EventContainer>
                            <ScoutingAccommodationFormContent />
                        </EventContainer>
                    </NwDrawer>
                )}
            </>
        ) : (
            <>
                {!isEdit && scoutingAccommodationData ? (
                    <div className="event-content-wrapper">
                        <EventDetailsContent />
                    </div>
                ) : (
                    <div className="event-content-wrapper">
                        <ScoutingAccommodationFormContent />
                    </div>
                )}
            </>
        );

    if ((!scoutingAccommodationData && scoutingAccommodationID) || status === 'Loading') {
        return <Loading textBlack />;
    } else {
        return plainMode ? (
            <EventContainer>
                {scoutingAccommodationData && (
                    <Row gutter={32}>
                        <Col span={14}>
                            <Content />
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: getEventLocationIframeUrl(
                                        scoutingAccommodationData,
                                        cachedList.countries.items
                                    ),
                                }}
                            />
                            <FlexContainer mg="8px 0 0">
                                <LocationUrl
                                    href={getEventDirectionUrl(scoutingAccommodationData, cachedList.countries.items)}
                                    target="_blank"
                                >
                                    {getEventDirectionUrl(scoutingAccommodationData, cachedList.countries.items)}
                                </LocationUrl>
                            </FlexContainer>
                        </Col>
                    </Row>
                )}
            </EventContainer>
        ) : (
            <EventContainer>
                <HeaderContent />
                <Content />
            </EventContainer>
        );
    }
};

export default ScoutingAccommodationEvent;
